import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Flower, Heart, ChatCircle, Image as ImageIcon, X, PaperPlaneRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import io from 'socket.io-client';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;

export default function Feed() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [newPostText, setNewPostText] = useState('');
  const [newPostImage, setNewPostImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [commentDialogs, setCommentDialogs] = useState({});
  const [comments, setComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const socketRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, feedRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/feed`, { withCredentials: true })
        ]);
        setUser(userRes.data);
        setPosts(feedRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();

    socketRef.current = io(SOCKET_URL);
    socketRef.current.on('new_post', (post) => {
      // Only add if not already in the feed (avoid duplicates from own posts)
      setPosts((prev) => {
        if (prev.some(p => p.post_id === post.post_id)) {
          return prev;
        }
        return [post, ...prev];
      });
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPostImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setNewPostImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadImage = async (file) => {
    try {
      console.log('Getting Cloudinary signature...');
      const sigResponse = await axios.get(`${API}/cloudinary/signature?resource_type=image&folder=chat`, {
        withCredentials: true
      });
      const sig = sigResponse.data;
      console.log('Signature received:', sig);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);

      console.log('Uploading to Cloudinary...');
      const uploadResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        formData
      );
      console.log('Cloudinary upload response:', uploadResponse.data);

      return uploadResponse.data.secure_url;
    } catch (error) {
      console.error('Image upload error:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  };

  const handleCreatePost = async () => {
    if (!newPostText.trim() && !newPostImage) {
      toast.error('Please add text or image');
      return;
    }

    try {
      setUploading(true);
      let imageUrl = null;

      if (newPostImage) {
        console.log('Uploading image...');
        imageUrl = await uploadImage(newPostImage);
        console.log('Image uploaded:', imageUrl);
      }

      console.log('Creating post with:', { text: newPostText.trim() || null, image_url: imageUrl });
      const response = await axios.post(
        `${API}/posts`,
        { text: newPostText.trim() || null, image_url: imageUrl },
        { withCredentials: true }
      );
      console.log('Post created:', response.data);

      // Add the new post to the top of the feed immediately
      setPosts((prevPosts) => [response.data, ...prevPosts]);

      setNewPostText('');
      clearImage();
      setCreateDialogOpen(false);
      toast.success('Post created!');
    } catch (error) {
      console.error('Create post error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`, {}, { withCredentials: true });
      setPosts(posts.map(post => {
        if (post.post_id === postId) {
          return {
            ...post,
            likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1,
            liked: response.data.liked
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      console.error('Load comments error:', error);
    }
  };

  const handleAddComment = async (postId) => {
    const commentText = newComments[postId];
    if (!commentText?.trim()) return;

    try {
      const response = await axios.post(
        `${API}/posts/${postId}/comments`,
        { text: commentText.trim() },
        { withCredentials: true }
      );
      
      setComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), response.data]
      }));
      
      setPosts(posts.map(post => {
        if (post.post_id === postId) {
          return { ...post, comments_count: post.comments_count + 1 };
        }
        return post;
      }));

      setNewComments(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      console.error('Add comment error:', error);
      toast.error('Failed to add comment');
    }
  };

  const openComments = (postId) => {
    setCommentDialogs(prev => ({ ...prev, [postId]: true }));
    if (!comments[postId]) {
      loadComments(postId);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 sticky top-0 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button 
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
            >
              ← Back
            </Button>
            <Flower size={28} weight="fill" className="text-primary" />
            <span className="text-xl font-heading font-bold">Community Feed</span>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-post-button"
                className="rounded-full bg-primary hover:bg-primary-hover font-bold"
              >
                Create Post
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Create a New Post</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Textarea
                  data-testid="post-text-input"
                  placeholder="Share something with the community..."
                  value={newPostText}
                  onChange={(e) => setNewPostText(e.target.value)}
                  className="rounded-xl min-h-32"
                  disabled={uploading}
                />
                
                {imagePreview && (
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="Preview" className="h-32 rounded-lg" />
                    <button
                      onClick={clearImage}
                      className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <div className="flex gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button
                    data-testid="upload-post-image-button"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    disabled={uploading}
                    className="flex-1"
                  >
                    <ImageIcon size={20} className="mr-2" />
                    Add Image
                  </Button>
                  <Button
                    data-testid="submit-post-button"
                    onClick={handleCreatePost}
                    disabled={uploading || (!newPostText.trim() && !newPostImage)}
                    className="flex-1 bg-primary hover:bg-primary-hover"
                  >
                    {uploading ? 'Posting...' : 'Post'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {posts.map((post, idx) => (
          <motion.div
            key={post.post_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-6 hover:shadow-md transition-all duration-300"
            data-testid={`post-${idx}`}
          >
            <div className="flex items-start gap-4 mb-4">
              <img src={post.user_picture} alt={post.user_name} className="w-12 h-12 rounded-full" />
              <div>
                <h3 className="font-heading font-semibold">{post.user_name}</h3>
                <p className="text-xs text-muted-foreground">
                  {new Date(post.created_at).toLocaleDateString()} at {new Date(post.created_at).toLocaleTimeString()}
                </p>
              </div>
            </div>

            {post.text && <p className="mb-4 leading-relaxed">{post.text}</p>}
            
            {post.image_url && (
              <img 
                src={post.image_url} 
                alt="Post" 
                className="w-full rounded-xl mb-4 cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(post.image_url, '_blank')}
              />
            )}

            <div className="flex items-center gap-6 pt-4 border-t border-border">
              <button
                data-testid={`like-button-${idx}`}
                onClick={() => handleLike(post.post_id)}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Heart size={20} weight={post.liked ? "fill" : "regular"} className={post.liked ? "text-primary" : ""} />
                <span className="text-sm font-medium">{post.likes_count}</span>
              </button>

              <button
                data-testid={`comment-button-${idx}`}
                onClick={() => openComments(post.post_id)}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <ChatCircle size={20} weight="regular" />
                <span className="text-sm font-medium">{post.comments_count}</span>
              </button>
            </div>

            <Dialog open={commentDialogs[post.post_id]} onOpenChange={(open) => setCommentDialogs(prev => ({ ...prev, [post.post_id]: open }))}>
              <DialogContent className="max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Comments</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {comments[post.post_id]?.map((comment) => (
                    <div key={comment.comment_id} className="flex gap-3">
                      <img src={comment.user_picture} alt={comment.user_name} className="w-8 h-8 rounded-full flex-shrink-0" />
                      <div className="flex-1 bg-muted rounded-xl p-3">
                        <p className="font-medium text-sm">{comment.user_name}</p>
                        <p className="text-sm mt-1">{comment.text}</p>
                      </div>
                    </div>
                  ))}
                  
                  {(!comments[post.post_id] || comments[post.post_id].length === 0) && (
                    <p className="text-center text-muted-foreground py-4">No comments yet. Be the first!</p>
                  )}

                  <div className="flex gap-2 pt-4 border-t">
                    <Input
                      data-testid={`comment-input-${idx}`}
                      placeholder="Add a comment..."
                      value={newComments[post.post_id] || ''}
                      onChange={(e) => setNewComments(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                      className="flex-1 rounded-xl"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.post_id)}
                    />
                    <Button
                      data-testid={`submit-comment-button-${idx}`}
                      onClick={() => handleAddComment(post.post_id)}
                      size="icon"
                      className="rounded-full bg-primary hover:bg-primary-hover flex-shrink-0 relative z-50"
                    >
                      <PaperPlaneRight size={18} weight="fill" />
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>
        ))}

        {posts.length === 0 && (
          <div className="text-center py-12">
            <Flower size={64} weight="fill" className="text-muted mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-2">No posts yet</h3>
            <p className="text-muted-foreground mb-6">Be the first to share something with the community!</p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="rounded-full bg-primary hover:bg-primary-hover font-bold"
            >
              Create First Post
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
